"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { countriesData } from "@/data/countriesData";
import { countryImageSrc } from "@/data/countryImages";
import CountryImage from "@/components/shared/CountryImage";
import { formatProse } from "@/lib/formatCopy";
const COUNTRIES_STYLE = `
    .country-grain::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E");
        pointer-events: none;
        z-index: 2;
    }
    
    .country-list-scroll::-webkit-scrollbar {
        width: 4px;
    }
    .country-list-scroll::-webkit-scrollbar-thumb {
        background: #0F1C2E22;
        border-radius: 4px;
    }

    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }
    .no-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    .accordion-panel {
        transition: flex 0.45s cubic-bezier(0.33, 1, 0.68, 1);
        contain: layout style;
    }

    .accordion-panel img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center 32%;
    }

    .accordion-panel.is-expanded img {
        object-position: center 28%;
    }

    .accordion-overlay-bottom {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        pointer-events: none;
        z-index: 3;
        background: linear-gradient(
            to top,
            rgba(15, 28, 46, 0.92) 0%,
            rgba(15, 28, 46, 0.55) 35%,
            rgba(15, 28, 46, 0.12) 65%,
            transparent 100%
        );
    }

    .accordion-overlay-bottom.is-expanded {
        height: 58%;
    }

    .accordion-overlay-bottom.is-collapsed {
        height: 72%;
    }

    .accordion-overlay-fold-dim {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 2;
        background: rgba(15, 28, 46, 0.22);
        transition: opacity 0.35s ease;
    }

    .accordion-overlay-fold-dim.is-expanded {
        opacity: 0;
    }

    .accordion-label-collapsed {
        transition: opacity 0.3s ease, transform 0.35s ease;
    }

    .accordion-content-expanded {
        transition: opacity 0.35s ease 0.05s, transform 0.35s ease 0.05s, max-height 0.45s ease;
    }

    .accordion-content-expanded.is-collapsed {
        transition: opacity 0.2s ease, transform 0.2s ease, max-height 0.35s ease;
    }
`;
const formatCardText = text => text ? formatProse(text) : "";
const countries = countriesData.map(c => ({
  slug: c.slug,
  name: c.name,
  label: c.region,
  year: c.periodStart || c.arrivalDate?.slice(-4) || "",
  arrivalDate: c.arrivalDate,
  desc: c.subtitle || c.description,
  cardImage: c.cardImage,
  heroImage: c.heroImage
}));
const DEFAULT_INDEX = Math.max(0, countries.findIndex(c => c.slug === "fiji"));
const VISIBLE_COUNT = 4;
const getWindowStart = (index, total, size = VISIBLE_COUNT) => {
  if (total <= size) return 0;
  // First page: always show 0–3 while index is within first group
  if (index < size) return 0;
  // Last page: show final group
  if (index >= total - size) return total - size;
  // Otherwise slide window so selected country stays visible
  return index;
};
const isInWindow = (index, start, size = VISIBLE_COUNT) => index >= start && index < start + size;
const Countries = () => {
  const $ = _c(116);
  const [activeIndex, setActiveIndex] = useState(DEFAULT_INDEX);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const listRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const itemRefs = useRef(t0);
  const mobileTrackRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mobileCardRefs = useRef(t1);
  const expandedIndex = hoveredIndex ?? activeIndex;
  const windowStart = getWindowStart(activeIndex, countries.length);
  let t2;
  if ($[2] !== windowStart) {
    t2 = Array.from({
      length: Math.min(VISIBLE_COUNT, countries.length)
    }, (_, i) => windowStart + i);
    $[2] = windowStart;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const visibleIndices = t2;
  let t3;
  let t4;
  if ($[4] !== activeIndex) {
    t3 = () => {
      const el = itemRefs.current[activeIndex];
      if (el && listRef.current) {
        el.scrollIntoView({
          behavior: "auto",
          block: "nearest"
        });
      }
    };
    t4 = [activeIndex];
    $[4] = activeIndex;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const handleListHover = index => {
    if (!isInWindow(index, windowStart, VISIBLE_COUNT)) {
      setActiveIndex(index);
      setHoveredIndex(null);
    } else {
      setHoveredIndex(index);
    }
  };
  let t5;
  if ($[7] !== hoveredIndex) {
    t5 = () => {
      if (hoveredIndex !== null) {
        setActiveIndex(hoveredIndex);
      }
      setHoveredIndex(null);
    };
    $[7] = hoveredIndex;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleSectionLeave = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = index_0 => {
      const next = (index_0 % countries.length + countries.length) % countries.length;
      setActiveIndex(next);
      setHoveredIndex(null);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const goTo = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = index_1 => {
      const next_0 = (index_1 % countries.length + countries.length) % countries.length;
      const card = mobileCardRefs.current[next_0];
      if (card) {
        card.scrollIntoView({
          behavior: "smooth",
          inline: "start",
          block: "nearest"
        });
      }
      setActiveIndex(next_0);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const scrollMobileToIndex = t7;
  let t8;
  if ($[11] !== activeIndex) {
    t8 = () => scrollMobileToIndex(activeIndex - 1);
    $[11] = activeIndex;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleMobilePrev = t8;
  let t9;
  if ($[13] !== activeIndex) {
    t9 = () => scrollMobileToIndex(activeIndex + 1);
    $[13] = activeIndex;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleMobileNext = t9;
  let t10;
  if ($[15] !== activeIndex) {
    t10 = () => goTo(activeIndex - 1);
    $[15] = activeIndex;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handlePrev = t10;
  let t11;
  if ($[17] !== activeIndex) {
    t11 = () => goTo(activeIndex + 1);
    $[17] = activeIndex;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleNext = t11;
  const scrollRafRef = useRef(0);
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const track = mobileTrackRef.current;
      if (!track) {
        return;
      }
      const viewportCenter = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let minDistance = Infinity;
      mobileCardRefs.current.forEach((card_0, index_2) => {
        if (!card_0) {
          return;
        }
        const cardCenter = card_0.offsetLeft + card_0.offsetWidth / 2;
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closest = index_2;
        }
      });
      setActiveIndex(prev => prev === closest ? prev : closest);
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const updateMobileIndexFromScroll = t12;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(updateMobileIndexFromScroll);
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const handleMobileScroll = t13;
  const mobileCountry = countries[activeIndex];
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <style>{COUNTRIES_STYLE}</style>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = "font-body bg-[#F4EFE6] py-16 sm:py-24 md:py-32 relative border-t border-[#0F1C2E]/05";
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="absolute inset-0 pointer-events-none"><div className="absolute left-[50%] top-0 bottom-0 w-px bg-[#0F1C2E]/[0.04]" /></div>;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const t17 = "max-w-[1320px] mx-auto px-4 sm:px-6 md:px-8";
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6"><div className="w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10.5px] tracking-[0.25em] uppercase text-accent-readable font-light">Global Diaspora</span></div>;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 sm:gap-8 mb-10 sm:mb-16 relative z-10"><div>{t18}<h2 className="font-display text-[34px] sm:text-[44px] md:text-[56px] font-light leading-[1.05] tracking-[-0.02em] text-[#0F1C2E]">Girmitiya <em className="text-accent-readable italic">Nations</em></h2></div><p className="font-body text-[14px] sm:text-[15px] font-light leading-[1.7] sm:leading-[1.85] text-[#4C596B] max-w-[400px]">Discover the nations where Indian indentured labourers built new lives and shaped vibrant cultures over generations.</p></div>;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div ref={mobileTrackRef} onScroll={handleMobileScroll} className="flex gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory scroll-smooth px-4 sm:px-6 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" style={{
      WebkitOverflowScrolling: "touch"
    }}>{countries.map((country, index_3) => <Link key={country.slug} ref={el_0 => {
        mobileCardRefs.current[index_3] = el_0;
      }} href={`/countries/${country.slug}`} className="group block shrink-0 grow-0 basis-full snap-start"><article className="flex flex-col bg-white border border-[#0F1C2E]/10 shadow-[0_8px_30px_rgba(15,28,46,0.08)] overflow-hidden"><div className="aspect-square w-full shrink-0 overflow-hidden border-b border-[#0F1C2E]/08"><CountryImage src={country.cardImage} alt={`${country.name}, Girmitiya nation card`} variant="card" className="w-full h-full" imgClassName="grayscale-[20%] sepia-[8%] group-hover:grayscale-0 group-hover:sepia-0 transition-[filter] duration-500" /></div><div className="relative flex flex-col gap-3 bg-[#FDFBF7] px-5 py-5 border-t border-[#0F1C2E]/08"><div className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#0F1C2E]/12 flex items-center justify-center bg-white shadow-sm z-[1]"><span className="text-[#0F1C2E] text-[15px] leading-none inline-block -rotate-45">→</span></div><p className="font-body text-[10px] tracking-[0.22em] uppercase text-accent-readable font-medium pr-12">Est. {country.arrivalDate || country.year}</p><div className="pr-12"><h3 className="font-display text-[26px] sm:text-[28px] text-[#0F1C2E] leading-[1.08]">{country.name}</h3>{country.label && <p className="font-body text-[11px] tracking-[0.14em] uppercase text-muted-readable mt-1.5 italic">({country.label})</p>}</div>{country.desc && <p className="font-body text-[13px] text-[#4C596B]/90 font-light leading-[1.65] line-clamp-4">{formatCardText(country.desc)}</p>}</div></article></Link>)}</div>;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const t21 = activeIndex === 0;
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ChevronLeft size={20} />;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== handleMobilePrev || $[28] !== t21) {
    t23 = <button type="button" onClick={handleMobilePrev} disabled={t21} className="w-11 h-11 shrink-0 rounded-full border border-[#0F1C2E]/20 flex items-center justify-center text-[#0F1C2E] active:bg-[#0F1C2E] active:text-white disabled:opacity-30 disabled:pointer-events-none transition-all" aria-label="Previous country">{t22}</button>;
    $[27] = handleMobilePrev;
    $[28] = t21;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  const t24 = String(activeIndex + 1);
  let t25;
  if ($[30] !== t24) {
    t25 = t24.padStart(2, "0");
    $[30] = t24;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  let t26;
  if ($[32] !== t25) {
    t26 = <span className="font-display text-[22px] text-[#0F1C2E] mr-1">{t25}</span>;
    $[32] = t25;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = String(countries.length).padStart(2, "0");
    $[34] = t27;
  } else {
    t27 = $[34];
  }
  let t28;
  if ($[35] !== mobileCountry.name) {
    t28 = <span className="block text-[#DF6D2A] mt-0.5 truncate max-w-[180px] mx-auto">{mobileCountry.name}</span>;
    $[35] = mobileCountry.name;
    $[36] = t28;
  } else {
    t28 = $[36];
  }
  let t29;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="block font-body text-[9px] tracking-[0.18em] uppercase text-[#4C596B]/70 mt-1">Swipe or use arrows</span>;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== t26 || $[39] !== t28) {
    t30 = <p className="font-body text-[12px] text-[#4C596B] text-center min-w-0 px-2">{t26}/ {t27}{t28}{t29}</p>;
    $[38] = t26;
    $[39] = t28;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  const t31 = activeIndex === countries.length - 1;
  let t32;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <ChevronRight size={20} />;
    $[41] = t32;
  } else {
    t32 = $[41];
  }
  let t33;
  if ($[42] !== handleMobileNext || $[43] !== t31) {
    t33 = <button type="button" onClick={handleMobileNext} disabled={t31} className="w-11 h-11 shrink-0 rounded-full border border-[#0F1C2E]/20 flex items-center justify-center text-[#0F1C2E] active:bg-[#0F1C2E] active:text-white disabled:opacity-30 disabled:pointer-events-none transition-all" aria-label="Next country">{t32}</button>;
    $[42] = handleMobileNext;
    $[43] = t31;
    $[44] = t33;
  } else {
    t33 = $[44];
  }
  let t34;
  if ($[45] !== t23 || $[46] !== t30 || $[47] !== t33) {
    t34 = <div className="flex items-center justify-between mt-5 h-[72px] px-4 sm:px-6">{t23}{t30}{t33}</div>;
    $[45] = t23;
    $[46] = t30;
    $[47] = t33;
    $[48] = t34;
  } else {
    t34 = $[48];
  }
  let t35;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="text-center mt-4 px-4 sm:px-6"><Link href="/countries" className="font-body text-[11px] tracking-[0.2em] uppercase text-[#DF6D2A] border-b border-[#DF6D2A]/30 pb-0.5">View all countries</Link></div>;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] !== t34) {
    t36 = <div className="lg:hidden relative z-10 -mx-4 sm:-mx-6">{t20}{t34}{t35}</div>;
    $[50] = t34;
    $[51] = t36;
  } else {
    t36 = $[51];
  }
  const t37 = "hidden lg:flex w-full relative z-10 h-[560px] gap-3";
  let t38;
  if ($[52] !== expandedIndex || $[53] !== visibleIndices) {
    t38 = <div className="flex-[1] flex gap-3 min-w-0 h-full">{visibleIndices.map(globalIndex => {
        const country_0 = countries[globalIndex];
        const isExpanded = expandedIndex === globalIndex;
        return <div key={country_0.slug} onMouseEnter={() => setHoveredIndex(globalIndex)} onClick={() => goTo(globalIndex)} className={`accordion-panel relative overflow-hidden cursor-pointer bg-[#0F1C2E] min-w-0 min-h-full ${isExpanded ? "is-expanded flex-[5] z-[2]" : "flex-[1] z-[1]"}`}><img src={countryImageSrc(country_0.heroImage)} alt={`${country_0.name}, Girmitiya nation heritage image`} loading="lazy" decoding="async" /><div className={`accordion-overlay-fold-dim ${isExpanded ? "is-expanded" : ""}`} /><div className={`accordion-overlay-bottom ${isExpanded ? "is-expanded" : "is-collapsed"}`} aria-hidden={true} /><div className="absolute inset-0 p-5 md:p-8 flex flex-col justify-between z-[5] pointer-events-none"><div className="relative w-full h-full flex flex-col justify-end"><div className={`accordion-label-collapsed absolute bottom-0 left-0 right-0 flex justify-center ${isExpanded ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}><span className="font-display text-[22px] font-light text-white tracking-wide whitespace-nowrap drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]" style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)"
                }}>{country_0.name}</span></div><div className={`accordion-content-expanded overflow-hidden pointer-events-auto ${isExpanded ? "opacity-100 translate-y-0 max-h-[300px]" : "is-collapsed opacity-0 translate-y-8 max-h-0"}`}><div className="flex items-end gap-3 mb-3"><h3 className="font-display text-[36px] md:text-[48px] font-light text-[#F4EFE6] leading-none">{country_0.name}</h3><span className="font-display text-[20px] md:text-[24px] font-light text-[#C9A84C] italic pb-1">{country_0.year}</span></div><p className="font-body text-[14px] md:text-[15px] font-light leading-[1.7] text-white/70 max-w-[340px] mb-5">{country_0.desc}</p><Link href={`/countries/${country_0.slug}`} className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.2em] uppercase text-[#C9A84C] hover:text-white transition-colors mb-4">Explore →</Link><div className={`w-12 h-px bg-[#C9A84C] transition-transform duration-500 delay-100 ease-out ${isExpanded ? "scale-x-100 origin-left" : "scale-x-0"}`} /></div></div></div></div>;
      })}</div>;
    $[52] = expandedIndex;
    $[53] = visibleIndices;
    $[54] = t38;
  } else {
    t38 = $[54];
  }
  const t39 = "w-[220px] shrink-0 flex flex-col h-full bg-white border border-[#0F1C2E]/10 shadow-[0_4px_24px_rgba(15,28,46,0.06)] overflow-hidden";
  let t40;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <p className="font-body text-[10px] tracking-[0.28em] uppercase text-[#DF6D2A] font-medium">All Nations</p>;
    $[55] = t40;
  } else {
    t40 = $[55];
  }
  let t41;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="px-5 py-5 border-b border-[#0F1C2E]/08 shrink-0 bg-[#FDFBF7]">{t40}<div className="flex items-end gap-2 mt-2"><span className="font-display text-[42px] leading-none text-[#0F1C2E]">{countries.length}</span><span className="font-body text-[10px] tracking-[0.15em] uppercase text-[#4C596B]/60 pb-1.5">Destinations</span></div></div>;
    $[56] = t41;
  } else {
    t41 = $[56];
  }
  const t42 = "country-list-scroll flex-1 overflow-y-auto py-1 bg-white";
  const t43 = countries.map((country_1, index_4) => {
    const isActive = activeIndex === index_4;
    const isPreview = hoveredIndex === index_4;
    return <button key={country_1.slug} ref={el_1 => {
      itemRefs.current[index_4] = el_1;
    }} type="button" onClick={() => goTo(index_4)} onMouseEnter={() => handleListHover(index_4)} className={`relative w-full text-left px-5 py-3.5 flex items-center gap-3 transition-colors duration-200 ${isActive || isPreview ? "bg-[#DF6D2A]/10" : "hover:bg-[#F4EFE6]"}`}>{isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#DF6D2A]" />}<span className={`font-mono text-[11px] shrink-0 w-5 ${isActive || isPreview ? "text-[#DF6D2A]" : "text-[#0F1C2E]/30"}`}>{String(index_4 + 1).padStart(2, "0")}</span><div className="min-w-0 flex-1"><p className={`font-display text-[17px] leading-tight truncate ${isActive || isPreview ? "text-[#0F1C2E]" : "text-[#4C596B]"}`}>{country_1.name}</p>{country_1.label && <p className="font-body text-[9px] tracking-[0.12em] uppercase text-[#4C596B]/50 mt-0.5 truncate">{country_1.label}</p>}</div>{isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#DF6D2A] shrink-0" />}</button>;
  });
  let t44;
  if ($[57] !== t43) {
    t44 = <div ref={listRef} className={t42}>{t43}</div>;
    $[57] = t43;
    $[58] = t44;
  } else {
    t44 = $[58];
  }
  let t45;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <ChevronLeft size={16} />;
    $[59] = t45;
  } else {
    t45 = $[59];
  }
  let t46;
  if ($[60] !== handlePrev) {
    t46 = <button type="button" onClick={handlePrev} className="w-8 h-8 rounded-full border border-[#0F1C2E]/15 flex items-center justify-center text-[#0F1C2E]/70 hover:bg-[#0F1C2E] hover:text-white hover:border-[#0F1C2E] transition-all" aria-label="Previous country">{t45}</button>;
    $[60] = handlePrev;
    $[61] = t46;
  } else {
    t46 = $[61];
  }
  const t47 = String(activeIndex + 1);
  let t48;
  if ($[62] !== t47) {
    t48 = t47.padStart(2, "0");
    $[62] = t47;
    $[63] = t48;
  } else {
    t48 = $[63];
  }
  let t49;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = String(countries.length).padStart(2, "0");
    $[64] = t49;
  } else {
    t49 = $[64];
  }
  let t50;
  if ($[65] !== t48) {
    t50 = <span className="font-body text-[10px] tracking-wider text-[#4C596B]/60">{t48} / {t49}</span>;
    $[65] = t48;
    $[66] = t50;
  } else {
    t50 = $[66];
  }
  let t51;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <ChevronRight size={16} />;
    $[67] = t51;
  } else {
    t51 = $[67];
  }
  let t52;
  if ($[68] !== handleNext) {
    t52 = <button type="button" onClick={handleNext} className="w-8 h-8 rounded-full border border-[#0F1C2E]/15 flex items-center justify-center text-[#0F1C2E]/70 hover:bg-[#0F1C2E] hover:text-white hover:border-[#0F1C2E] transition-all" aria-label="Next country">{t51}</button>;
    $[68] = handleNext;
    $[69] = t52;
  } else {
    t52 = $[69];
  }
  let t53;
  if ($[70] !== t46 || $[71] !== t50 || $[72] !== t52) {
    t53 = <div className="px-5 py-3.5 border-t border-[#0F1C2E]/08 shrink-0 flex items-center justify-between gap-2 bg-[#FDFBF7]">{t46}{t50}{t52}</div>;
    $[70] = t46;
    $[71] = t50;
    $[72] = t52;
    $[73] = t53;
  } else {
    t53 = $[73];
  }
  let t54;
  if ($[74] !== t41 || $[75] !== t44 || $[76] !== t53) {
    t54 = <div className={t39}>{t41}{t44}{t53}</div>;
    $[74] = t41;
    $[75] = t44;
    $[76] = t53;
    $[77] = t54;
  } else {
    t54 = $[77];
  }
  let t55;
  if ($[78] !== handleSectionLeave || $[79] !== t38 || $[80] !== t54) {
    t55 = <div className={t37} onMouseLeave={handleSectionLeave}>{t38}{t54}</div>;
    $[78] = handleSectionLeave;
    $[79] = t38;
    $[80] = t54;
    $[81] = t55;
  } else {
    t55 = $[81];
  }
  let t56;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <ChevronLeft size={20} />;
    $[82] = t56;
  } else {
    t56 = $[82];
  }
  let t57;
  if ($[83] !== handlePrev) {
    t57 = <button type="button" onClick={handlePrev} className="w-11 h-11 rounded-full border border-[#0F1C2E]/20 flex items-center justify-center text-[#0F1C2E] hover:bg-[#0F1C2E] hover:text-white hover:border-[#0F1C2E] transition-all duration-300" aria-label="Previous country">{t56}</button>;
    $[83] = handlePrev;
    $[84] = t57;
  } else {
    t57 = $[84];
  }
  let t58;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <ChevronRight size={20} />;
    $[85] = t58;
  } else {
    t58 = $[85];
  }
  let t59;
  if ($[86] !== handleNext) {
    t59 = <button type="button" onClick={handleNext} className="w-11 h-11 rounded-full border border-[#0F1C2E]/20 flex items-center justify-center text-[#0F1C2E] hover:bg-[#0F1C2E] hover:text-white hover:border-[#0F1C2E] transition-all duration-300" aria-label="Next country">{t58}</button>;
    $[86] = handleNext;
    $[87] = t59;
  } else {
    t59 = $[87];
  }
  let t60;
  if ($[88] !== t57 || $[89] !== t59) {
    t60 = <div className="flex items-center gap-4">{t57}{t59}</div>;
    $[88] = t57;
    $[89] = t59;
    $[90] = t60;
  } else {
    t60 = $[90];
  }
  const t61 = String(activeIndex + 1);
  let t62;
  if ($[91] !== t61) {
    t62 = t61.padStart(2, "0");
    $[91] = t61;
    $[92] = t62;
  } else {
    t62 = $[92];
  }
  let t63;
  if ($[93] !== t62) {
    t63 = <span className="font-display text-[28px] text-[#0F1C2E] mr-2">{t62}</span>;
    $[93] = t62;
    $[94] = t63;
  } else {
    t63 = $[94];
  }
  let t64;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = String(countries.length).padStart(2, "0");
    $[95] = t64;
  } else {
    t64 = $[95];
  }
  const t65 = countries[activeIndex];
  let t66;
  if ($[96] !== t65.name) {
    t66 = <span className="text-[#DF6D2A]">{t65.name}</span>;
    $[96] = t65.name;
    $[97] = t66;
  } else {
    t66 = $[97];
  }
  let t67;
  if ($[98] !== t63 || $[99] !== t66) {
    t67 = <p className="font-body text-[13px] text-[#4C596B]">{t63}/ {t64}{" "}{t66}</p>;
    $[98] = t63;
    $[99] = t66;
    $[100] = t67;
  } else {
    t67 = $[100];
  }
  let t68;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <Link href="/countries" className="font-body text-[11px] tracking-[0.2em] uppercase text-[#DF6D2A] hover:text-[#0F1C2E] transition-colors border-b border-[#DF6D2A]/30 pb-0.5">View all countries</Link>;
    $[101] = t68;
  } else {
    t68 = $[101];
  }
  let t69;
  if ($[102] !== t60 || $[103] !== t67) {
    t69 = <div className="hidden lg:flex items-center justify-between mt-6 relative z-10">{t60}{t67}{t68}</div>;
    $[102] = t60;
    $[103] = t67;
    $[104] = t69;
  } else {
    t69 = $[104];
  }
  let t70;
  if ($[105] !== t19 || $[106] !== t36 || $[107] !== t55 || $[108] !== t69) {
    t70 = <div className={t17}>{t19}{t36}{t55}{t69}</div>;
    $[105] = t19;
    $[106] = t36;
    $[107] = t55;
    $[108] = t69;
    $[109] = t70;
  } else {
    t70 = $[109];
  }
  let t71;
  if ($[110] !== t16 || $[111] !== t70) {
    t71 = <section className={t15}>{t16}{t70}</section>;
    $[110] = t16;
    $[111] = t70;
    $[112] = t71;
  } else {
    t71 = $[112];
  }
  let t72;
  if ($[113] !== t14 || $[114] !== t71) {
    t72 = <>{t14}{t71}</>;
    $[113] = t14;
    $[114] = t71;
    $[115] = t72;
  } else {
    t72 = $[115];
  }
  return t72;
};
export default Countries;